import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSmartBack } from '../../shared/navigation';

import GridWrapper from '../../shared/components/layout/GridWrapper';
import Button from '../../shared/components/ui/Button';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import {
  getVendorQuatationItem,
  createPO,
  updateQuotationStatus,
  getRFQById
} from '../services/api/implementation/impl';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useQuery } from '@tanstack/react-query';
import noDataIcon from '../../shared/assets/png/noDataFound.png';
import toast from 'react-hot-toast';
import Loader from '../../shared/components/ui/Loader';
import DeliveryDetailsCard from '../components/ui/DeliveryDetailsCard';
import Modal from '../../shared/components/ui/Modal';
import {
  MAX_DOCUMENT_MB,
  PO_DOCUMENT_TYPES,
  uploadPODocumentFiles,
  validatePODocumentFiles
} from '../utils/poDocuments';

const OrdersRequirement = () => {
  const userId = Cookies.get('userId');
  const navigate = useNavigate();
  const { id } = useParams();
  const { collapsed } = useSidebar();
  const { onBack } = useSmartBack({ fallback: '/builder/inventory/orders' });

  // Numeric helpers
  const toNumberOrZero = (val) => {
    const num = Number(val);
    return Number.isFinite(num) ? num : 0;
  };
  const getExtraChargesTotal = (q) => {
    if (Array.isArray(q?.extraCharges)) {
      return q.extraCharges.reduce((sum, ch) => sum + toNumberOrZero(ch?.amount), 0);
    }
    return toNumberOrZero(q?.extraChargesAmount ?? q?.extraCharges);
  };
  const getShippingCharges = (q) => toNumberOrZero(q?.shippingCharges);
  const GST_RATE_EXTRA_FREIGHT = 0.18;
  const getGstOnExtraAndFreight = (q) =>
    Math.round((getExtraChargesTotal(q) + getShippingCharges(q)) * GST_RATE_EXTRA_FREIGHT);

  const {
    data: GetQuatationItem,
    refetch: refetchGetQuatationItem,
    isFetching: isFetchingGetQuatationItem
  } = useQuery({
    queryKey: ['quotationItem', parseInt(id)],
    queryFn: () => getVendorQuatationItem(parseInt(id)),
    enabled: !!parseInt(id)
  });

  const {
    data: getRFQByIdData,
    refetch: refetchGetRFQById,
    isFetching: isFetchingGetRFQById
  } = useQuery({
    queryKey: ['rfq', parseInt(id)],
    queryFn: () => getRFQById(parseInt(id)),
    keepPreviousData: true
  });

  useEffect(() => {
    if (id) {
      refetchGetQuatationItem();
      refetchGetRFQById();
    }
  }, []);

  const [selected, setSelected] = useState({}); // { [quotationId]: Set of itemIds }
  // Accept goes through a confirm step where each vendor's PO can carry the builder's own bill / quote.
  const [acceptDraft, setAcceptDraft] = useState(null); // [{ id, items }] while the step is open
  const [acceptDocs, setAcceptDocs] = useState({}); // { [quotationId]: { type, files } }
  const [accepting, setAccepting] = useState(false);

  // Transform data for table
  const quotations = useMemo(() => {
    // If response is a single object, wrap in array
    if (!GetQuatationItem) return [];
    if (Array.isArray(GetQuatationItem)) {
      // If array of quotations
      return GetQuatationItem;
    } else {
      // If single quotation object
      return [GetQuatationItem];
    }
  }, [GetQuatationItem]); // Initialize with best prices selected

  // Check if we should show RFQ data (when quotations are empty)
  const shouldShowRFQData = useMemo(() => {
    return quotations.length === 0 && getRFQByIdData;
  }, [quotations.length, getRFQByIdData]);

  // Get RFQ items and vendors for display
  const rfqItems = useMemo(() => {
    if (!shouldShowRFQData) return [];
    return getRFQByIdData?.rfqItems || [];
  }, [shouldShowRFQData, getRFQByIdData]);

  const rfqVendors = useMemo(() => {
    if (!shouldShowRFQData) return [];
    return getRFQByIdData?.vendors || [];
  }, [shouldShowRFQData, getRFQByIdData]);

  // Get all unique items across all quotations
  const allItems = useMemo(() => {
    const itemMap = {};
    quotations.forEach((q) => {
      (q.items || []).forEach((item) => {
        if (!itemMap[item.item_id]) {
          itemMap[item.item_id] = {
            ...item.item,
            quantity: item.quantity
          };
        }
      });
    });
    return Object.values(itemMap);
  }, [quotations]);

  // For each item, find the minimum baseAmount across all vendors
  const minValuePerItem = useMemo(() => {
    const minMap = {};
    allItems.forEach((item) => {
      let min = undefined;
      quotations.forEach((q) => {
        const quoteItem = (q.items || []).find((i) => i.item_id === item.id);
        if (quoteItem) {
          if (min === undefined || quoteItem.baseAmount < min) {
            min = quoteItem.baseAmount;
          }
        }
      });
      minMap[item.id] = min;
    });
    return minMap;
  }, [allItems, quotations]);

  // Calculate discount for each quotation
  const calculateDiscount = useMemo(() => {
    const discountMap = {};
    quotations.forEach((q) => {
      // Sum all item total amounts
      const itemsTotal = (q.items || []).reduce((sum, item) => sum + (item.baseAmount || 0), 0);

      // Add extra charges and shipping charges
      const extraCharges = q.extraCharges || 0;
      const shippingCharges = q.shippingCharges || 0;

      // Calculate total before discount
      const totalBeforeDiscount = itemsTotal + extraCharges + shippingCharges;

      // Calculate discount as percentage (divide by 100)
      const discountPercentage = Math.round((totalBeforeDiscount * q.discount) / 100);
      const baseAmount = totalBeforeDiscount - discountPercentage;
      discountMap[q.id] = { discountPercentage, baseAmount };
    });
    return discountMap;
  }, [quotations]);
  useEffect(() => {
    if (quotations.length > 0 && allItems.length > 0) {
      const initialSelection = {};

      allItems.forEach((item) => {
        const minPrice = minValuePerItem[item.id];
        if (minPrice !== undefined) {
          // Find the vendor with the minimum price for this item
          quotations.forEach((q) => {
            const quoteItem = (q.items || []).find((i) => i.item_id === item.id);
            if (quoteItem && quoteItem.baseAmount === minPrice) {
              if (!initialSelection[q.id]) {
                initialSelection[q.id] = new Set();
              }
              initialSelection[q.id].add(quoteItem.id);
            }
          });
        }
      });

      setSelected(initialSelection);
    }
  }, [quotations, allItems, minValuePerItem]);

  // Toggle cell selection
  const toggleCell = (quotationId, quotationItemId) => {
    setSelected((prev) => {
      const set = new Set(prev[quotationId] || []);

      // If this item is already selected in this vendor, remove it
      if (set.has(quotationItemId)) {
        set.delete(quotationItemId);
        return { ...prev, [quotationId]: set };
      }

      // Find the item details to get the item_id
      const quotation = quotations.find((q) => q.id === quotationId);
      const quoteItem = quotation?.items?.find((item) => item.id === quotationItemId);
      const itemId = quoteItem?.item_id;

      if (!itemId) return prev;

      // Remove this item from all other vendors first
      const newSelected = { ...prev };
      Object.keys(newSelected).forEach((vendorId) => {
        if (vendorId !== quotationId.toString()) {
          const vendorSet = new Set(newSelected[vendorId] || []);
          // Find and remove any item with the same item_id from other vendors
          const vendorQuotation = quotations.find((q) => q.id === parseInt(vendorId));
          vendorQuotation?.items?.forEach((item) => {
            if (item.item_id === itemId) {
              vendorSet.delete(item.id);
            }
          });
          newSelected[vendorId] = vendorSet;
        }
      });

      // Add the item to the current vendor
      set.add(quotationItemId);
      newSelected[quotationId] = set;

      return newSelected;
    });
  };

  // Handle Accept button: opens the confirm step rather than accepting straight away
  const handleAccept = () => {
    const accepted = Object.entries(selected)
      .filter(([_, itemSet]) => itemSet.size > 0)
      .map(([quotationId, itemSet]) => ({
        id: Number(quotationId),
        items: Array.from(itemSet).map((id) => ({ id }))
      }));
    if (!accepted.length) {
      toast.error('Please select at least one item to accept.');
      return;
    }
    setAcceptDocs({});
    setAcceptDraft(accepted);
  };

  const setAcceptDoc = (quotationId, patch) =>
    setAcceptDocs((prev) => ({
      ...prev,
      [quotationId]: { type: 'quotation', files: [], ...prev[quotationId], ...patch }
    }));

  const confirmAccept = async () => {
    for (const q of acceptDraft) {
      const invalid = validatePODocumentFiles(Array.from(acceptDocs[q.id]?.files || []));
      if (invalid) {
        toast.error(invalid);
        return;
      }
    }
    setAccepting(true);
    try {
      // Files go to S3 first and ride along in the accept call, so the PO is
      // created with its documents rather than needing a second request.
      const quotationsPayload = await Promise.all(
        acceptDraft.map(async (q) => {
          const files = Array.from(acceptDocs[q.id]?.files || []);
          if (!files.length) return q;
          const type = acceptDocs[q.id]?.type || 'quotation';
          return { ...q, documents: await uploadPODocumentFiles(`quotation-${q.id}`, files, type) };
        })
      );
      const response = await createPO({ quotations: quotationsPayload });
      toast.success('Quotation accepted successfully');
      const attachedToPendingPO = quotationsPayload.some(
        (q) => q.documents?.length && !(response || []).find((r) => r.quotationId === q.id)?.po
      );
      if (attachedToPendingPO) {
        toast('Documents will show on the PO once the advance is cleared.');
      }
      setAcceptDraft(null);
      setSelected({});
      await refetchGetQuatationItem();
    } catch (error) {
      console.error('Error accepting quotation:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Error accepting quotation');
    } finally {
      setAccepting(false);
    }
  };

  // Add handler for status change
  const handleStatusChange = async (quotationId, status) => {
    try {
      await updateQuotationStatus(quotationId, { status });
      toast.success(
        `Quotation ${status === 'Rejected' ? 'rejected' : 'sent for negotiation'} successfully`
      );
      await refetchGetQuatationItem();
      // Optionally refetch data here
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || 'Error updating quotation status'
      );
      console.error('Error updating quotation status:', error);
    }
  };

  // Helper to get all item IDs
  const allItemIds = useMemo(() => allItems.map((item) => item.id), [allItems]);

  // Get selected item by quotation
  const getSelectedItems = useMemo(() => {
    return Object.entries(selected).reduce((acc, [qId, itemSet]) => {
      const quotation = quotations.find((q) => q.id === Number(qId));
      if (!quotation) return acc;

      const selectedItems = quotation.items?.filter((item) => itemSet.has(item.id)) || [];
      acc[qId] = selectedItems;
      return acc;
    }, {});
  }, [selected, quotations]);

  // Calculate Best Column values
  const bestColumn = useMemo(() => {
    const bestPerItem = {};

    // Build selection map: item_id -> selected quotation item
    const selectionByItemId = {};
    Object.entries(selected).forEach(([qId, itemSet]) => {
      const quotation = quotations.find((q) => q.id === Number(qId));
      if (!quotation) return;
      (quotation.items || []).forEach((it) => {
        if (itemSet && itemSet.has(it.id)) {
          selectionByItemId[it.item_id] = it;
        }
      });
    });

    // Best price per item: prefer selected, otherwise min
    allItems.forEach((item) => {
      let selectedPrice = 0;

      // If selected manually, take selected quote item price
      if (selectionByItemId[item.id]) {
        selectedPrice = toNumberOrZero(selectionByItemId[item.id].baseAmount);
      }

      if (selectedPrice > 0) {
        bestPerItem[item.id] = selectedPrice;
      } else {
        // Default to min price across vendors for this item
        const min = quotations.reduce((acc, q) => {
          const qi = (q.items || []).find((i) => i.item_id === item.id);
          if (!qi) return acc;
          const price = toNumberOrZero(qi.baseAmount);
          return acc === undefined || price < acc ? price : acc;
        }, undefined);
        bestPerItem[item.id] = toNumberOrZero(min);
      }
    });

    // GST: sum gstAmount of items selected from different vendors.
    // If nothing is selected, fallback to minimum total GST across vendors.
    let bestGST = 0;
    const selectedItemsArray = Object.values(selectionByItemId);
    if (selectedItemsArray.length > 0) {
      bestGST = selectedItemsArray.reduce((sum, it) => sum + toNumberOrZero(it.gstAmount), 0);
    } else {
      const minGSTAcrossVendors = quotations.reduce((acc, q) => {
        const totalGST = (q.items || []).reduce((sum, it) => sum + toNumberOrZero(it.gstAmount), 0);
        return acc === undefined || totalGST < acc ? totalGST : acc;
      }, undefined);
      bestGST = toNumberOrZero(minGSTAcrossVendors);
    }

    // Sum extra + shipping from selected vendors only
    let extra = 0;
    let shipping = 0;
    let gstOnExtraFreight = 0;

    quotations.forEach((q) => {
      if (selected[q.id] && selected[q.id].size > 0) {
        const e = getExtraChargesTotal(q);
        const s = getShippingCharges(q);
        extra += e;
        shipping += s;
        gstOnExtraFreight += Math.round((e + s) * GST_RATE_EXTRA_FREIGHT);
      }
    });

    // Total in Best column = sum of all best column values
    const sumBestItems = Object.values(bestPerItem).reduce(
      (sum, val) => sum + toNumberOrZero(val),
      0
    );
    const baseAmount = sumBestItems + bestGST + extra + shipping + gstOnExtraFreight;

    return {
      bestPerItem,
      bestGST,
      extra,
      shipping,
      gstOnExtraFreight,
      baseAmount
    };
  }, [selected, quotations, allItems]);

  /* 
      JSX
    */
  return (
    <>
      <Loader isVisible={isFetchingGetQuatationItem || accepting} />
      <Layout>
        {/* Header */}
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header data={{ name: `Requirement ` }} backArrow={true} onBack={onBack} />
        </div>

        {/* Grid */}
        <GridWrapper width={100}>
          <div className="mt-[6rem]">
            <DeliveryDetailsCard
              rfq={getRFQByIdData}
              fallbackDestination={getRFQByIdData?.project?.name || '—'}
              className="mb-4"
            />
            <div className="rounded-t-lg bg-white">
            <div className="overflow-x-auto bg-white rounded-t-xl">
              <table className="w-full rounded-t-xl text-left border border-collapse border-gray-200">
                <thead className="bg-gray-200 text-gray-600">
                  <tr className="font-normal h-[3rem]">
                    <th className="px-3 py-2 border border-gray-200 font-normal">Item</th>
                    {shouldShowRFQData
                      ? // Show RFQ vendors
                        rfqVendors.map((vendorData) => (
                          <th
                            key={vendorData.id}
                            className="px-3 py-2 border border-gray-200 font-normal">
                            <div className="flex items-center gap-2">
                              <span>
                                {vendorData.vendor?.user?.name ||
                                  vendorData.vendor?.businessName ||
                                  'Vendor'}
                              </span>
                            </div>
                          </th>
                        ))
                      : // Show quotation vendors
                        quotations.map((q) => {
                          const isRowDisabled = q.rfq?.status === 'Completed';
                          const isChecked = (q.items || []).every((item) =>
                            selected[q.id]?.has(item.id)
                          );
                          return (
                            <th key={q.id} className="px-3 py-2 border border-gray-200 font-normal">
                              <div className="flex items-center gap-2">
                                {!isRowDisabled && (
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {
                                      setSelected((prev) => {
                                        const newSet = new Set();
                                        if (!isChecked) {
                                          // If selecting all items from this vendor, remove the same items from other vendors first
                                          const newSelected = { ...prev };
                                          q.items?.forEach((item) => {
                                            // Remove this item from all other vendors
                                            Object.keys(newSelected).forEach((vendorId) => {
                                              if (vendorId !== q.id.toString()) {
                                                const vendorSet = new Set(
                                                  newSelected[vendorId] || []
                                                );
                                                const vendorQuotation = quotations.find(
                                                  (quotation) => quotation.id === parseInt(vendorId)
                                                );
                                                vendorQuotation?.items?.forEach((vendorItem) => {
                                                  if (vendorItem.item_id === item.item_id) {
                                                    vendorSet.delete(vendorItem.id);
                                                  }
                                                });
                                                newSelected[vendorId] = vendorSet;
                                              }
                                            });
                                            newSet.add(item.id);
                                          });
                                          newSelected[q.id] = newSet;
                                          return newSelected;
                                        }
                                        return { ...prev, [q.id]: newSet };
                                      });
                                    }}
                                  />
                                )}
                                <span>
                                  {q.vendor?.user?.name || q.vendor?.businessName || 'Vendor'}
                                </span>
                              </div>
                            </th>
                          );
                        })}
                    {!shouldShowRFQData && (
                      <th className="px-3 py-2 border border-gray-200 font-normal text-center">
                        Best
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody
                  className={
                    quotations.some((q) => q.rfq?.status === 'Completed')
                      ? 'opacity-60 pointer-events-none select-none'
                      : ''
                  }>
                  {shouldShowRFQData
                    ? // Show RFQ items
                      rfqItems.map((rfqItem) => (
                        <tr key={rfqItem.id} className="bg-white h-[3.5rem]">
                          <td className="px-4 py-2 text-sm font-semibold border border-gray-200">
                            {rfqItem.item?.item_name} ({rfqItem.quantity} {rfqItem.unit})
                          </td>
                          {rfqVendors.map((vendorData) => (
                            <td
                              key={vendorData.id}
                              className="px-4 py-2 text-sm border border-gray-200">
                              <div className="flex items-center justify-center">
                                <span className="text-gray-500">Pending</span>
                              </div>
                            </td>
                          ))}
                        </tr>
                      ))
                    : // Show quotation items
                      allItems.map((item) => (
                        <tr key={item.id} className="bg-white h-[3.5rem]">
                          <td className="px-4 py-2 text-sm font-semibold border border-gray-200">
                            {item.item_name} ({item.quantity} {item.unit})
                          </td>
                          {quotations.map((q) => {
                            const quoteItem = (q.items || []).find((i) => i.item_id === item.id);
                            const isMin =
                              quoteItem && quoteItem.baseAmount === minValuePerItem[item.id];
                            const checked =
                              quoteItem && (selected[q.id] || new Set()).has(quoteItem.id);
                            const isRowDisabled = [
                              'Rejected',
                              'Negotiation',
                              'Accepted',
                              'Expired',
                              'Partially Accepted',
                              'Price Updated'
                            ].includes(q.status);

                            return (
                              <td
                                key={q.id}
                                className={`px-4 py-2 text-sm border border-gray-200 relative ${
                                  isRowDisabled ? 'opacity-60 pointer-events-none select-none' : ''
                                }`}
                                style={{
                                  background: isMin
                                    ? 'linear-gradient(to right, #f0f9f0 0%, #e8f5e8 50%, #d4f3d4 100%)'
                                    : undefined
                                }}>
                                <div className="flex items-center gap-2 relative">
                                  {quoteItem && q.rfq?.status !== 'Completed' && (
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => toggleCell(q.id, quoteItem.id)}
                                      style={{
                                        position: 'absolute',
                                        top: 4,
                                        left: 4,
                                        backgroundColor: checked ? '#8be09b' : 'white',
                                        borderColor: checked ? '#8be09b' : '#d1d5db',
                                        accentColor: '#8be09b'
                                      }}
                                    />
                                  )}
                                  <span style={{ marginLeft: 24 }}>
                                    {quoteItem
                                      ? `₹${new Intl.NumberFormat('en-IN').format(quoteItem.baseAmount)}`
                                      : '-'}
                                  </span>
                                </div>
                              </td>
                            );
                          })}
                          <td className="px-4 py-2 text-sm font-semibold border border-gray-200 bg-green-50 text-center">
                            {(() => {
                              // Find which vendor has this item selected
                              let selectedPrice = 0;
                              let selectedVendorName = '';

                              quotations.forEach((q) => {
                                const quoteItem = (q.items || []).find(
                                  (i) => i.item_id === item.id
                                );
                                if (quoteItem && selected[q.id]?.has(quoteItem.id)) {
                                  selectedPrice = quoteItem.baseAmount;
                                  selectedVendorName =
                                    q.vendor?.user?.name || q.vendor?.businessName || 'Vendor';
                                }
                              });

                              return selectedPrice > 0 ? (
                                <div className="text-center">
                                  <div className="font-semibold">
                                    ₹{new Intl.NumberFormat('en-IN').format(selectedPrice)}
                                  </div>
                                  <div className="text-xs text-gray-600 mt-1">
                                    {selectedVendorName}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-400">No selection</span>
                              );
                            })()}
                          </td>
                        </tr>
                      ))}

                  {/* Extra details rows - only show for quotations */}
                  {!shouldShowRFQData &&
                    [
                      {
                        label: 'GST',
                        getValue: (q) => {
                          // Calculate total GST for all items in this quotation
                          const totalGST = (q.items || []).reduce((sum, item) => {
                            return sum + (item.gstAmount || 0);
                          }, 0);
                          return totalGST;
                        },
                        best: bestColumn.bestGST
                      },
                      {
                        label: 'Extra Charges',
                        getValue: (q) => getExtraChargesTotal(q),
                        best: bestColumn.extra
                      },
                      {
                        label: 'Freight Charges',
                        getValue: (q) => getShippingCharges(q),
                        best: bestColumn.shipping
                      },
                      {
                        label: 'GST For (Extra charges and Freight charge)',
                        getValue: (q) => getGstOnExtraAndFreight(q),
                        best: bestColumn.gstOnExtraFreight
                      },

                      {
                        label: 'Total Amount',
                        getValue: (q) => {
                          const baseAmount = toNumberOrZero(q.total_amount);
                          const extraCharges = getExtraChargesTotal(q);
                          const shippingCharges = getShippingCharges(q);
                          const gstExtraFreight = Math.round(
                            (extraCharges + shippingCharges) * GST_RATE_EXTRA_FREIGHT
                          );
                          return baseAmount + extraCharges + shippingCharges + gstExtraFreight;
                        },
                        best: bestColumn.baseAmount
                      }
                    ].map((row, index) => (
                      <tr key={index} className="bg-white h-[3.5rem]">
                        <td className="px-4 py-2 text-sm font-semibold border border-gray-200">
                          {row.label}
                        </td>
                        {quotations.map((q) => (
                          <td
                            key={q.id}
                            className="px-4 py-2 text-sm font-semibold border border-gray-200">
                            {row.getValue(q)
                              ? `₹${new Intl.NumberFormat('en-IN').format(row.getValue(q))}`
                              : '-'}
                          </td>
                        ))}
                        <td className="px-4 py-2 text-sm font-semibold border border-gray-200 bg-green-50 text-center">
                          ₹{new Intl.NumberFormat('en-IN').format(row.best || 0)}
                        </td>
                      </tr>
                    ))}

                  {/* Actions row - only show for quotations */}
                  {!shouldShowRFQData && (
                    <tr className="bg-white h-[3.5rem]">
                      <td className="px-4 py-2 text-sm font-semibold border border-gray-200">
                        Actions
                      </td>
                      {quotations.map((q) => {
                        const isRowDisabled = [
                          'Rejected',
                          'Negotiation',
                          'Accepted',
                          'Expired',
                          'Partially Accepted',
                          'Price Updated'
                        ].includes(q.status);
                        const isAllItemsSelected = (q.items || []).every((item) =>
                          selected[q.id]?.has(item.id)
                        );
                        const hasMultipleVendorsSelected =
                          Object.keys(selected).filter(
                            (key) => selected[key] && selected[key].size > 0
                          ).length > 1;

                        return (
                          <td key={q.id} className="text-sm py-2 px-2 border border-gray-200">
                            <div className="flex gap-2">
                              {isRowDisabled ? (
                                <span className="font-semibold capitalize text-gray-500">
                                  {q.status === 'Price Updated' || q.status === 'Partially Accepted'
                                    ? 'Accepted'
                                    : q.status}
                                </span>
                              ) : isAllItemsSelected && !hasMultipleVendorsSelected ? (
                                // Show Accept button when all items from this vendor are selected and no other vendors have selections
                                <Button
                                  type="button"
                                  onClick={handleAccept}
                                  customStyle={{
                                    minWidth: 100,
                                    background: '#2A565E',
                                    color: 'white'
                                  }}>
                                  Accept
                                </Button>
                              ) : (
                                // Show Reject and Negotiate buttons
                                <>
                                  <button
                                    type="button"
                                    className="border border-[#fca5a5] text-[#dc2626] bg-white rounded-md px-2 py-1 transition duration-150 hover:bg-[#dc2626] hover:text-white shadow-sm"
                                    onClick={() => handleStatusChange(q.id, 'Rejected')}>
                                    Reject
                                  </button>
                                  <button
                                    type="button"
                                    className="border border-[#2A565E] text-[#2A565E] bg-white rounded-md px-2 py-1 transition duration-150 hover:bg-[#2A565E] hover:text-white shadow-sm"
                                    onClick={() => handleStatusChange(q.id, 'Negotiation')}>
                                    Negotiate
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        );
                      })}
                      <td className="text-sm py-2 px-2 border border-gray-200 bg-green-50 text-center">
                        {/* Show Accept button in last column by default, or when multiple vendors have selections */}
                        {(() => {
                          // Check if RFQ is completed
                          const isRFQCompleted = quotations.some(
                            (q) => q.rfq?.status === 'Completed'
                          );

                          if (isRFQCompleted) {
                            return null; // Hide Accept button if RFQ is completed
                          }

                          const hasMultipleVendorsSelected =
                            Object.keys(selected).filter(
                              (key) => selected[key] && selected[key].size > 0
                            ).length > 1;
                          const hasAnySelections = Object.values(selected).some(
                            (set) => set && set.size > 0
                          );
                          const hasSingleVendorAllSelected = Object.keys(selected).some((key) => {
                            const quotation = quotations.find((q) => q.id === parseInt(key));
                            return (
                              quotation &&
                              (quotation.items || []).every((item) => selected[key]?.has(item.id))
                            );
                          });

                          // Show Accept button if: no selections, multiple vendors selected, or not all items from single vendor selected
                          return !hasSingleVendorAllSelected ? (
                            <Button
                              type="button"
                              onClick={handleAccept}
                              customStyle={{
                                minWidth: 100,
                                background: '#2A565E',
                                color: 'white'
                              }}>
                              Accept
                            </Button>
                          ) : null;
                        })()}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            </div>
          </div>
        </GridWrapper>

        {acceptDraft && (
          <Modal
            name="Accept quotation"
            width={46}
            onClose={() => !accepting && setAcceptDraft(null)}>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Optionally attach your own bill or quotation to each vendor&apos;s PO (PDF or image,
                up to {MAX_DOCUMENT_MB} MB each). You can add more later from the PO page.
              </p>
              {acceptDraft.map((q) => {
                const quotation = quotations.find((x) => x.id === q.id);
                const doc = acceptDocs[q.id] || {};
                return (
                  <div key={q.id} className="rounded-lg border border-gray-200 p-3">
                    <p className="text-sm font-semibold text-gray-800">
                      {quotation?.vendor?.user?.name || quotation?.vendor?.businessName || 'Vendor'}
                      <span className="font-normal text-gray-500"> · {q.items.length} item(s)</span>
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <select
                        value={doc.type || 'quotation'}
                        onChange={(e) => setAcceptDoc(q.id, { type: e.target.value })}
                        className="h-9 rounded-lg border border-gray-200 bg-white px-2 text-sm outline-none focus:border-[#2A565E]">
                        {PO_DOCUMENT_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="file"
                        accept="application/pdf,image/*"
                        multiple
                        onChange={(e) => setAcceptDoc(q.id, { files: e.target.files || [] })}
                        className="text-sm text-gray-600"
                      />
                    </div>
                    {doc.files?.length > 0 && (
                      <p className="mt-1 text-xs text-gray-500">{doc.files.length} file(s) selected</p>
                    )}
                  </div>
                );
              })}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  disabled={accepting}
                  onClick={() => setAcceptDraft(null)}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-60">
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={accepting}
                  onClick={confirmAccept}
                  className="rounded-lg bg-[#2A565E] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                  {accepting ? 'Accepting…' : 'Accept'}
                </button>
              </div>
            </div>
          </Modal>
        )}
      </Layout>
    </>
  );
};

export default OrdersRequirement;
